import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Input,
  InputLabel,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';

import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PulseLoader from 'react-spinners/PulseLoader';

import { setCredentials } from './authSlice';
import { useLoginMutation } from './authApiSlice';
import usePersist from '../../hooks/usePersist';
import { useSendResetEmailMutation } from '../users/usersApiSlice';
import GoogleSignInButton from './GoogleSignInButton';

import { tokens } from '../../theme';
import { usfuel } from '../../assets';


const LoginForm = ({
  onSuccess,
  showBackHome = false,
  passwordLoginDefaultOpen = true,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const dispatch = useDispatch();

  const userRef = useRef(null);
  const errRef = useRef(null);

  const [username, setUsername] =
    useState('');

  const [password, setPassword] =
    useState('');

  const [errMsg, setErrMsg] =
    useState('');

  const [persist, setPersist] =
    usePersist();

  const [
    showForgotPassword,
    setShowForgotPassword,
  ] = useState(false);

  const [
    emailForReset,
    setEmailForReset,
  ] = useState('');

  const [
    showPasswordLogin,
    setShowPasswordLogin,
  ] = useState(
    passwordLoginDefaultOpen
  );

  const [
    login,
    {
      isLoading,
    },
  ] = useLoginMutation();

  const [
    sendResetEmail,
    {
      isLoading: isSendingReset,
    },
  ] = useSendResetEmailMutation();


  useEffect(() => {
    if (
      showPasswordLogin
    ) {
      userRef.current?.focus();
    }
  }, [
    showPasswordLogin,
  ]);


  useEffect(() => {
    setErrMsg('');
  }, [
    username,
    password,
  ]);


  const handleSubmit =
    async (event) => {
      event.preventDefault();

      /*
       * Cancel a Google One Tap prompt if one
       * happens to exist before password auth.
       */
      window.google
        ?.accounts
        ?.id
        ?.cancel?.();

      try {
        const {
          accessToken,
          isFirstLogin,
        } =
          await login({
            username,
            password,
          }).unwrap();

        dispatch(
          setCredentials({
            accessToken,
          })
        );

        setUsername('');
        setPassword('');
        setErrMsg('');

        /*
         * LoginForm itself does NOT decide
         * where the user goes.
         *
         * Login.jsx can send them to /console.
         * Navbar/LoginDialog can either:
         *
         * - leave them on the public site
         * - send them to /console
         */
        onSuccess?.({
          isFirstLogin:
            Boolean(
              isFirstLogin
            ),
        });
      } catch (err) {
        if (!err?.status) {
          setErrMsg(
            'No Server Response'
          );
        } else if (
          err.status === 400
        ) {
          setErrMsg(
            'Missing Username or Password'
          );
        } else if (
          err.status === 401
        ) {
          setErrMsg(
            'Unauthorized'
          );
        } else {
          setErrMsg(
            err?.data?.message ||
            'Login failed'
          );
        }

        errRef.current?.focus();
      }
    };


  const handleToggle =
    () => {
      setPersist(
        (previous) =>
          !previous
      );
    };


  const handleSendResetEmail =
    async () => {
      try {
        await sendResetEmail(
          emailForReset
        ).unwrap();

        alert(
          'Reset email sent successfully. Please check your inbox.'
        );

        setShowForgotPassword(
          false
        );

        setEmailForReset('');
      } catch (error) {
        alert(
          'Failed to send reset email.'
        );
      }
    };


  return (
    <Paper
      elevation={6}
      sx={{
        p: {
          xs: 3,
          sm: 5,
        },

        width:
          '100%',

        maxWidth:
          '400px',

        textAlign:
          'center',

        boxShadow:
          '0px 0px 10px rgba(0, 0, 0, 1)',

        backgroundColor:
          colors.grey[200],

        borderRadius:
          '20px',
      }}
    >
      {/* LOGO */}

      <Box
        sx={{
          display:
            'flex',

          justifyContent:
            'center',

          mb:
            2,
        }}
      >
        <img
          src={usfuel}
          alt="USFuel Logo"
          style={{
            width:
              '200px',
          }}
        />
      </Box>


      {/* TITLE */}

      <Typography
        variant="h2"
        color={
          colors.blueAccent[700]
        }
        gutterBottom
        sx={{
          fontFamily:
            theme.typography
              .fontFamily,
        }}
      >
        Login
      </Typography>


      {/* GOOGLE LOGIN */}

      <GoogleSignInButton />


      {/* DIVIDER */}

      <Box
        sx={{
          display:
            'flex',

          alignItems:
            'center',

          gap:
            2,

          my:
            3,
        }}
      >
        <Box
          sx={{
            height:
              '1px',

            flex:
              1,

            backgroundColor:
              'rgba(0,0,0,0.16)',
          }}
        />

        <Typography
          sx={{
            color:
              colors.grey[600],

            fontSize:
              '0.85rem',

            fontWeight:
              700,
          }}
        >
          OR
        </Typography>

        <Box
          sx={{
            height:
              '1px',

            flex:
              1,

            backgroundColor:
              'rgba(0,0,0,0.16)',
          }}
        />
      </Box>


      {/* PASSWORD LOGIN TOGGLE */}

      {!showPasswordLogin && (
        <Button
          type="button"
          fullWidth
          variant="outlined"
          onClick={
            () =>
              setShowPasswordLogin(
                true
              )
          }
          sx={{
            fontFamily:
              'Jura, sans-serif',

            fontSize:
              '15px',

            fontWeight:
              700,

            color:
              colors.primary[700],

            borderColor:
              'rgba(0,0,0,0.24)',

            borderRadius:
              '10px',

            py:
              1.25,

            '&:hover': {
              borderColor:
                colors.primary[600],

              backgroundColor:
                'rgba(0,0,0,0.04)',
            },
          }}
        >
          Sign in with username & password
        </Button>
      )}


      {/* PASSWORD LOGIN */}

      {showPasswordLogin && (
        <>
          <Box
            component="form"
            onSubmit={
              handleSubmit
            }
            id="login-form"
          >
            <FormControl
              fullWidth
              sx={{
                mb:
                  2,
              }}
            >
              <InputLabel
                htmlFor="username"
                sx={{
                  fontSize:
                    '18px',

                  color:
                    colors.primary[600],
                }}
              >
                Username or Email
              </InputLabel>

              <Input
                id="username"
                name="username"
                type="text"
                value={
                  username
                }
                onChange={
                  (event) =>
                    setUsername(
                      event.target.value
                    )
                }
                autoComplete="username"
                required
                inputRef={
                  userRef
                }
              />
            </FormControl>


            <FormControl
              fullWidth
              sx={{
                mb:
                  3,
              }}
            >
              <InputLabel
                htmlFor="password"
                sx={{
                  fontSize:
                    '18px',

                  color:
                    colors.primary[600],
                }}
              >
                Password
              </InputLabel>

              <Input
                id="password"
                name="password"
                type="password"
                value={
                  password
                }
                onChange={
                  (event) =>
                    setPassword(
                      event.target.value
                    )
                }
                autoComplete="current-password"
                required
              />
            </FormControl>


            <Box
              sx={{
                display:
                  'flex',

                justifyContent:
                  'center',

                mb:
                  2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      persist
                    }
                    onChange={
                      handleToggle
                    }
                    color="primary"
                  />
                }
                label="Trust This Device"
              />
            </Box>


            <Typography
              variant="body2"
              color="error"
              ref={
                errRef
              }
              tabIndex={-1}
              sx={{
                mb:
                  2,

                minHeight:
                  errMsg
                    ? '20px'
                    : 0,
              }}
            >
              {errMsg}
            </Typography>


            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={
                isLoading
              }
              sx={{
                mt:
                  1,

                fontFamily:
                  'Jura, sans-serif',

                fontSize:
                  '16px',

                backgroundColor:
                  colors.primary[500],

                '&:hover': {
                  backgroundColor:
                    colors.primary[700],
                },
              }}
            >
              {isLoading ? (
                <PulseLoader
                  size={
                    8
                  }
                  color="#FFF"
                />
              ) : (
                'Sign In'
              )}
            </Button>
          </Box>


          {/* FORGOT PASSWORD */}

          <Box
            sx={{
              mt:
                2,
            }}
          >
            {showForgotPassword ? (
              <>
                <FormControl
                  fullWidth
                  sx={{
                    mb:
                      2,
                  }}
                >
                  <InputLabel
                    htmlFor="emailForReset"
                    sx={{
                      fontSize:
                        '18px',

                      color:
                        colors.primary[600],
                    }}
                  >
                    Email
                  </InputLabel>

                  <Input
                    id="emailForReset"
                    type="email"
                    value={
                      emailForReset
                    }
                    onChange={
                      (
                        event
                      ) =>
                        setEmailForReset(
                          event
                            .target
                            .value
                        )
                    }
                    required
                  />
                </FormControl>


                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={
                    handleSendResetEmail
                  }
                  disabled={
                    isSendingReset
                  }
                >
                  {isSendingReset
                    ? 'Sending...'
                    : 'Send Reset Email'}
                </Button>


                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={
                    () =>
                      setShowForgotPassword(
                        false
                      )
                  }
                  sx={{
                    mt:
                      1,
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                fullWidth
                variant="text"
                onClick={
                  () =>
                    setShowForgotPassword(
                      true
                    )
                }
              >
                Forgot Password?
              </Button>
            )}
          </Box>


          {/*
     * Popup only:
     * allow them to collapse password auth
     * and go back to the simple Google view.
     */}
          {!passwordLoginDefaultOpen && (
            <Button
              type="button"
              fullWidth
              variant="text"
              onClick={
                () => {
                  setShowPasswordLogin(
                    false
                  );

                  setShowForgotPassword(
                    false
                  );

                  setErrMsg(
                    ''
                  );
                }
              }
              sx={{
                mt:
                  1,

                color:
                  colors.grey[600],

                fontSize:
                  '0.8rem',
              }}
            >
              Back to Google sign-in
            </Button>
          )}
        </>
      )}


      {/* OPTIONAL HOME LINK */}

      {showBackHome && (
        <Box
          sx={{
            mt:
              3,
          }}
        >
          <Link
            to="/"
            style={{
              fontFamily:
                'Jura, sans-serif',

              textDecoration:
                'none',

              color:
                colors.blueAccent[700],
            }}
          >
            Back to Home
          </Link>
        </Box>
      )}
    </Paper>
  );
};


export default LoginForm;